import { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useSignIn } from 'react-auth-kit';
import { useUpdateAtom } from 'jotai/utils';

import { Button, Input, Logo, Paper, Select } from 'src/components';
import {
  completeSsoSignup,
  exchangeSsoCode,
  isSsoSignupPrefill,
  SsoLoginResponse,
  SsoSignupPrefill,
} from 'src/api/functions/ssoApi';
import { isErrorResponseResult } from 'src/api/utils/errorHandlers';
import { addonsSettingsAtom } from 'src/shared/settings/addons';
import { ACTION_TYPES } from 'src/shared/settings/base';
import messages from './messages';
import { sanitizeNext } from 'src/utils/nextPath';
import baseStyles from 'src/pages/Onboarding/styles.module.scss';
import formStyles from 'src/pages/Signup/DemoSignup.module.scss';
import styles from './styles.module.scss';

// /login/sso - where the backend sends the browser after the identity
// provider round-trip (filespin/api/sso.py SSOCallbackHandler):
//
//   ?code=..&next=/assets            login: exchange the code, sign in, go to `next`
//   ?code=..&next=..&signup=1        new account: show the finish-signup form first
//   ?error=<code>                    something failed: explain, offer to retry
//
// The code is single-use and expires in about a minute (ten for signup), so
// a refresh of this page lands on "That sign-in link expired" - by design.

type Phase =
  | { kind: 'working' }
  | { kind: 'error'; code: string }
  | { kind: 'signup'; prefill: SsoSignupPrefill }
  | { kind: 'done' };

const SIGNUP_CODES = new Set(['SIGNUP_REJECTED']);

function errorCodeOf(r: unknown): string {
  if (!isErrorResponseResult(r)) return 'sso_error';
  try {
    const raw = (r as any).error;
    const err = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return String(err?.error?.code || 'sso_error').toLowerCase();
  } catch {
    return 'sso_error';
  }
}

function minutesUntil(expEpochSeconds?: number, fallbackMinutes = 1440): number {
  if (!expEpochSeconds) return fallbackMinutes;
  const mins = Math.floor((expEpochSeconds * 1000 - Date.now()) / 60000);
  return mins > 1 ? mins : fallbackMinutes;
}

export const SSOLogin = () => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const location = useLocation();
  const signIn = useSignIn();
  const updateAddonsSettings = useUpdateAtom(addonsSettingsAtom);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const code = (params.get('code') || '').trim();
  const next = sanitizeNext(params.get('next'));
  const errorParam = (params.get('error') || '').trim();

  const [phase, setPhase] = useState<Phase>(errorParam ? { kind: 'error', code: errorParam } : { kind: 'working' });
  const [form, setForm] = useState({ firstName: '', lastName: '', industry: 'general' });
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const started = useRef(false);

  const finishLogin = (resp: SsoLoginResponse, signedUp = false) => {
    const ok = signIn({
      token: resp.jwt,
      expiresIn: minutesUntil(resp.exp),
      tokenType: 'Bearer',
      authState: {
        isLoggedIn: true,
        accessId: resp.accessID,
        uploadkey: resp.uploadkey,
        role: resp.role_name,
        roleId: resp.role_id,
        permissions: resp.permissions,
        notifications: resp.notifications,
        preferences: resp.preferences,
        username: resp.username,
        userEmail: resp.user_email,
        userId: resp.user_id,
      },
    });
    if (!ok) {
      setPhase({ kind: 'error', code: 'sso_error' });
      return;
    }
    if (resp.ai_assistant_available) {
      updateAddonsSettings({
        type: ACTION_TYPES.UPDATE_STATE,
        payload: { AI_ASSISTANT: { added: true, enabled: true } },
      });
    }
    setPhase({ kind: 'done' });
    if (signedUp) {
      // A brand-new account goes through the onboarding wizard (Setup, demo,
      // jump-off). The Profile step is skipped: the IdP supplied the name and
      // there is no password to set. Same hand-off shape as DemoSignup.
      history.replace('/onboarding', {
        fromSsoSignup: true,
        profileSnapshot: {
          email: resp.user_email,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          newPassword: '',
        },
        orgName: resp.org_name,
        industry: resp.industry,
        aiAssistantAvailable: resp.ai_assistant_available,
      });
      return;
    }
    history.replace(next);
  };

  useEffect(() => {
    if (errorParam || started.current) return;
    started.current = true; // React 18 strict-mode double mount must not burn the code
    if (!code) {
      setPhase({ kind: 'error', code: 'code_invalid' });
      return;
    }
    (async () => {
      const r = await exchangeSsoCode(code);
      if (isErrorResponseResult(r)) {
        setPhase({ kind: 'error', code: errorCodeOf(r) });
        return;
      }
      if (isSsoSignupPrefill(r)) {
        setForm({
          firstName: r.first_name || '',
          lastName: r.last_name || '',
          industry: r.industries?.some((o) => o.value === 'general') ? 'general' : r.industries?.[0]?.value || 'general',
        });
        setPhase({ kind: 'signup', prefill: r });
        return;
      }
      // A ?signup=1 URL whose code resolved to a login (e.g. the account was
      // created meanwhile) is simply a login.
      finishLogin(r as SsoLoginResponse);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, errorParam]);

  const submitSignup = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (phase.kind !== 'signup') return;
    setFormError(null);
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setFormError(formatMessage(messages.ssoNameRequired));
      return;
    }
    setSubmitting(true);
    const r = await completeSsoSignup({
      code,
      first_name: form.firstName.trim(),
      last_name: form.lastName.trim(),
      industry: form.industry,
    });
    setSubmitting(false);
    if (isErrorResponseResult(r)) {
      const c = errorCodeOf(r);
      if (c === 'invalid_industry') {
        setFormError(formatMessage(messages.ssoIndustryRequired));
        return;
      }
      setPhase({ kind: 'error', code: c });
      return;
    }
    finishLogin(r as SsoLoginResponse, true);
  };

  const errorText = (c: string) => {
    switch (c) {
      case 'idp_error':
        return formatMessage(messages.ssoErrorCancelled);
      case 'email_unverified':
        return formatMessage(messages.ssoErrorEmailUnverified);
      case 'account_disabled':
        return formatMessage(messages.ssoErrorAccountDisabled);
      case 'signup_rejected':
        return formatMessage(messages.ssoErrorSignupRejected);
      case 'no_account':
        return formatMessage(messages.ssoErrorNoAccount);
      case 'code_invalid':
      case 'state_invalid':
        return formatMessage(messages.ssoErrorExpired);
      default:
        return formatMessage(messages.ssoErrorGeneric);
    }
  };

  if (phase.kind === 'error') {
    const rejected = SIGNUP_CODES.has(phase.code.toUpperCase());
    return (
      <div className={styles.formWrapper}>
        <div className={styles.form} data-test="sso-error">
          <div className={styles.logoContainer}>
            <Logo linkable={false} />
          </div>
          <p className={styles.ssoMessage}>{errorText(phase.code)}</p>
          {phase.code === 'no_account' && (
            <p className={styles.ssoMessage}>
              <Link to="/signup">
                <FormattedMessage {...messages.ssoCreateAccountLink} />
              </Link>
            </p>
          )}
          {rejected && (
            <p className={styles.ssoMessage}>
              <a href="https://filespin.io/book-a-demo">
                <FormattedMessage {...messages.ssoContactSales} />
              </a>
            </p>
          )}
          <div className={styles.buttonWrapper}>
            <Button size="small" onClick={() => history.replace('/login')}>
              <FormattedMessage {...messages.ssoBackToSignIn} />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (phase.kind === 'signup') {
    const { prefill } = phase;
    const options = prefill.industries?.length ? prefill.industries : [{ value: 'general', label: 'General' }];
    return (
      <main className={baseStyles.onboarding}>
        <header>
          <Logo />
        </header>
        <section className={baseStyles.container}>
          <Paper className={baseStyles.content}>
            <h1>
              <FormattedMessage {...messages.ssoFinishTitle} values={{ brand: <span>FileSpin</span> }} />
            </h1>
            <p>
              <FormattedMessage {...messages.ssoFinishIntro} />
            </p>
            <form className={formStyles.form} onSubmit={submitSignup} data-test="sso-finish-signup">
              <Input label={formatMessage(messages.email)} variant="secondary" value={prefill.email} onChange={() => {}} disabled />
              <div className={formStyles.nameRow}>
                <Input
                  label={formatMessage(messages.ssoFirstName)}
                  variant="secondary"
                  value={form.firstName}
                  onChange={(v) => setForm({ ...form, firstName: v })}
                />
                <Input
                  label={formatMessage(messages.ssoLastName)}
                  variant="secondary"
                  value={form.lastName}
                  onChange={(v) => setForm({ ...form, lastName: v })}
                />
              </div>
              <Select
                label={formatMessage(messages.ssoIndustry)}
                value={form.industry}
                onChange={(v) => setForm({ ...form, industry: v })}
                options={options}
              />
              {formError && <p className={formStyles.error}>{formError}</p>}
              <Button type="submit" loading={submitting}>
                <FormattedMessage {...messages.ssoCreateAccount} />
              </Button>
              <p className={styles.ssoFinePrint}>
                <FormattedMessage {...messages.ssoTermsNote} />{' '}
                <a href="https://filespin.io/terms">Terms of Service</a> &middot;{' '}
                <a href="https://filespin.io/privacy/">Privacy Policy</a>
              </p>
            </form>
          </Paper>
        </section>
      </main>
    );
  }

  return (
    <div className={styles.formWrapper}>
      <div className={styles.form} data-test="sso-working">
        <div className={styles.logoContainer}>
          <Logo linkable={false} />
        </div>
        <p className={styles.ssoMessage}>
          <FormattedMessage {...messages.ssoSigningIn} />
        </p>
      </div>
    </div>
  );
};

export default SSOLogin;
